import moment from 'moment-mini';
import * as XLSX from 'xlsx';

import { XlsxSheet, XlsxWritableCellValue } from '../../../shared/types/xlsx';

/** One sheet to write into the file: only text and numbers can be written into its cells */
type ExportedSheet = XlsxSheet<XlsxWritableCellValue>;

const MAX_SHEET_NAME_LENGTH = 31;
const INVALID_SHEET_NAME_CHARS = /[[\]:*?/\\]/g;

/**
 * Sheet name accepted by excel.
 * The invalid characters are replaced, the name is cut to the maximum length,
 * and a collision is resolved with an index.
 *
 * @param {string} name the wanted name of the sheet,
 * e. g. the identifier (shortName) of the factory
 * @param {Set<string>} usedSheetNames the names already taken in the workbook,
 * extended with the returned one
 * @returns {string} the name to give to the sheet
 */
function getSheetName(name: string, usedSheetNames: Set<string>): string {
  const sanitizedSheetName = name
    .replace(INVALID_SHEET_NAME_CHARS, '_')
    .slice(0, MAX_SHEET_NAME_LENGTH);

  let sheetName = sanitizedSheetName;
  let index = 2;
  while (usedSheetNames.has(sheetName)) {
    const suffix = `_${index++}`;
    sheetName = sanitizedSheetName.slice(0, MAX_SHEET_NAME_LENGTH - suffix.length) + suffix;
  }
  usedSheetNames.add(sheetName);

  return sheetName;
}

/**
 * Builds the workbook of the given sheets.
 *
 * @param {ExportedSheet[]} sheets name and rows of every sheet,
 * in the wanted order of the worksheets
 * @returns {XLSX.WorkBook} the workbook ready to be written
 */
export function buildFreightCostExportWorkbook(sheets: ExportedSheet[]): XLSX.WorkBook {
  const workbook = XLSX.utils.book_new();
  const usedSheetNames = new Set<string>();

  for (const sheet of sheets) {
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.aoa_to_sheet(sheet.rows),
      getSheetName(sheet.name, usedSheetNames)
    );
  }

  return workbook;
}

/**
 * Downloads the given sheets as a timestamped xlsx file.
 *
 * @param {ExportedSheet[]} sheets name and rows of every sheet,
 * in the wanted order of the worksheets
 */
export default function downloadFreightCostExport(sheets: ExportedSheet[]): void {
  XLSX.writeFile(
    buildFreightCostExportWorkbook(sheets),
    'gyárankénti_fuvardíjak_' + moment(new Date()).format('YYYYMMDD_HHmm') + '.xlsx',
    { bookType: 'xlsx', type: 'buffer' }
  );
}
